import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Colors } from "../../config/Constants";

const Checked = require("../../assets/images/checked.png");
const Unchecked = require("../../assets/images/non-checked.png");
const Close = require("../../assets/images/close.png");
const downArrow = require("../../assets/images/VectorDownArrow.png");

// Types
type Option = {
  label: string;
  value: string;
};

type MultiSelectDropdownProps = {
  label?: string;
  required?: boolean;
  data: Option[];
  selected: string[];
  onToggleOption: (value: string) => void;
  onToggleSelectAll: () => void;
  error?: string;
  disabled?: boolean;
};

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  required,
  data,
  selected,
  onToggleOption,
  onToggleSelectAll,
  error,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isAllSelected = selected.length === data.length;

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  const getSelectedOptions = () =>
    data.filter((opt) => selected.includes(opt.value));

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <DropdownContainer ref={dropdownRef}>
      {label && (
        <Label>
          {label}
          {required && <span>*</span>}
        </Label>
      )}
      <DropdownHeader
        onClick={disabled ? () => {} : toggleDropdown}
        isPlaceholder={selected.length === 0}
        error={!!error}
      >
        <ContentWrapper>
          {selected.length === 0 ? (
            <span className="placeholder">Select options</span>
          ) : (
            <ChipsWrapper>
              {getSelectedOptions().map((item) => (
                <Chip key={item.value}>
                  {item.label}
                  <img
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleOption(item.value);
                    }}
                    src={Close}
                    alt="Remove"
                    style={{ width: "1rem", height: "1rem", cursor: "pointer" }}
                  />
                </Chip>
              ))}
            </ChipsWrapper>
          )}
        </ContentWrapper>
        <ArrowIcon src={downArrow} isOpen={isOpen} />
      </DropdownHeader>

      {isOpen && (
        <DropdownList>
          <DropdownItem
            style={{ borderBottom: `1px solid ${Colors.athensGray}` }}
            onClick={onToggleSelectAll}
          >
            <img
              style={{ width: "1rem", height: "1rem" }}
              src={isAllSelected ? Checked : Unchecked}
              alt="Select All"
            />
            <span>Select All Tags</span>
          </DropdownItem>
          {data.map((option) => (
            <DropdownItem
              key={option.value}
              onClick={() => onToggleOption(option.value)}
            >
              <img
                style={{ width: "1rem", height: "1rem" }}
                src={selected.includes(option.value) ? Checked : Unchecked}
                alt="checkbox"
              />
              <span>{option.label}</span>
            </DropdownItem>
          ))}
        </DropdownList>
      )}
      {error && <Error>{error}</Error>}
    </DropdownContainer>
  );
};

export default MultiSelectDropdown;

const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownHeader = styled.div<{
  error: boolean;
  isPlaceholder: boolean;
}>`
  padding: 0.5rem 0.5rem;
  background: white;
  border: 1px solid ${(props) => (props.error ? Colors.red : Colors.athensGray)};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  color: ${(props) => (props.isPlaceholder ? Colors.cloudyGrey : "#000")};
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  min-height: 1.5rem;

  .placeholder {
    color: ${Colors.cloudyGrey};
  }
`;

const ChipsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Chip = styled.div`
  background-color: ${Colors.black}29;
  color: ${Colors.black};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.25rem;
`;

const DropdownList = styled.div`
  position: absolute;
  width: 100%;
  top: 105%;
  background: white;

  box-shadow: 0px 3px 14px 2px #0000001f;

  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
`;

const DropdownItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: ${Colors.red}0A;
  }

  input {
    cursor: pointer;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ArrowIcon = styled.img<{ isOpen: boolean }>`
  width: 0.75rem;
  height: 0.5rem;
  transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.2s;
`;

const Label = styled.label`
  font-family: "Poppins-Medium";
  font-style: normal;
  font-weight: 350;
  font-size: 13px;
  line-height: 16px;
  color: ${Colors.electricBlue};
  margin-bottom: 0.5rem;
  > span {
    color: ${Colors.red};
    margin-left: 2px;
  }
`;

const Error = styled.span`
  color: ${Colors.red};
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;
